// The order — PCPartPicker's list page for a listening room: one line per
// thing to buy, where it is bought, and what it really costs: its base price,
// less any promo, plus shipping and tax (owner, 2026-09-27: "when a user is
// purchasing something, they care about where they buy it from").
//
// Derived from the document like the parts list it follows (ADR 011: a build
// is derived, not stored). The parts are the system sheet's chosen models —
// the same model in two slots is one purchase — then the cables. What the
// owner decides about buying lives in `doc.order`: the store, their own price
// per lot (a pair, or one), a promo and shipping per line, and one sales-tax
// rate; anything they have not said is its default. Tax is charged on the
// price after the promo, not on shipping.
//
// Cables have no list price — the catalogue does not carry them — so they
// count toward the total only once the owner gives them one.

import type { EditorDoc, OrderLine } from '@roomtreatment/core';
import {
  DEFAULT_RETAILER, isRetailerId, type Product, type RetailerId,
} from '@roomtreatment/av-catalog';
import type { CableNeeds } from './cables';
import type { SystemSheet } from './systemSheet';

export type OrderItemKind = 'product' | 'wire' | 'sub-cable';

export interface OrderItem {
  /** `doc.order.lines`' key: the product id, or `wire` / `sub-cable`. */
  key: string;
  kind: OrderItemKind;
  product: Product | null;
  name: string;
  /** How many physical units, and the lots they are bought in. */
  units: number;
  lots: number;
  /** What one lot is: a pair, one, or a length of cable. */
  lot: 'pair' | 'each' | 'length';
  /** The list price per lot, or null (a cable: no list price). */
  listLotUsd: number | null;
  /** What one lot costs the owner: their own price, else the list price. */
  lotUsd: number | null;
  priceEdited: boolean;
  base: number | null;
  promo: number;
  shipping: number;
  tax: number;
  /** base − promo + shipping + tax; null while the line has no price. */
  total: number | null;
  where: RetailerId;
  /** Still made, or discontinued (the used market's). Null for a cable. */
  availability: 'current' | 'discontinued' | null;
  /** For a store search when there is no product: "14 AWG speaker wire". */
  query: string | null;
}

export interface OrderSheet {
  items: OrderItem[];
  taxPct: number;
  totals: { base: number; promo: number; shipping: number; tax: number; total: number };
  /** Lines with no price yet (a cable nobody has priced). */
  unpriced: number;
}

const cents = (v: number) => Math.round(v * 100) / 100;

function lineOf(doc: EditorDoc, key: string): OrderLine {
  return doc.order?.lines?.[key] ?? {};
}

export function orderSheet(
  doc: EditorDoc,
  sheet: Pick<SystemSheet, 'rows'>,
  cables: CableNeeds,
): OrderSheet {
  const taxPct = doc.order?.taxPct ?? 0;

  const price = (
    base: Omit<OrderItem, 'lotUsd' | 'priceEdited' | 'base' | 'promo' | 'shipping' | 'tax' | 'total' | 'where'>,
  ): OrderItem => {
    const line = lineOf(doc, base.key);
    const lotUsd = line.lotUsd ?? base.listLotUsd;
    const lineBase = lotUsd === null ? null : cents(lotUsd * base.lots);
    const promo = lineBase === null ? 0 : Math.min(line.promoUsd ?? 0, lineBase);
    const shipping = line.shippingUsd ?? 0;
    const tax = lineBase === null ? 0 : cents(((lineBase - promo) * taxPct) / 100);
    return {
      ...base,
      lotUsd,
      priceEdited: line.lotUsd !== undefined && line.lotUsd !== base.listLotUsd,
      base: lineBase,
      promo,
      shipping,
      tax,
      total: lineBase === null ? null : cents(lineBase - promo + shipping + tax),
      where: isRetailerId(line.where) ? line.where : DEFAULT_RETAILER,
    };
  };

  // One purchase per model, however many slots it fills.
  const byProduct = new Map<string, { product: Product; units: number }>();
  for (const row of sheet.rows) {
    if (!row.product) continue;
    const had = byProduct.get(row.product.id);
    if (had) had.units += row.units;
    else byProduct.set(row.product.id, { product: row.product, units: row.units });
  }

  const items: OrderItem[] = [];
  for (const { product, units } of byProduct.values()) {
    const pair = product.sellUnit === 'pair';
    items.push(price({
      key: product.id,
      kind: 'product',
      product,
      name: `${product.brand} ${product.model}`,
      units,
      lots: pair ? Math.ceil(units / 2) : units,
      lot: pair ? 'pair' : 'each',
      listLotUsd: product.msrpUsd.v,
      availability: product.status,
      query: null,
    }));
  }

  if (cables.speakerWire) {
    const { gauge } = cables.speakerWire;
    items.push(price({
      key: 'wire',
      kind: 'wire',
      product: null,
      name: `${gauge} AWG speaker wire`,
      units: 1,
      lots: 1,
      lot: 'length',
      listLotUsd: null,
      availability: null,
      query: `${gauge} AWG speaker wire`,
    }));
  }
  if (cables.subCable) {
    const { count } = cables.subCable;
    items.push(price({
      key: 'sub-cable',
      kind: 'sub-cable',
      product: null,
      name: count === 1 ? 'Subwoofer cable' : 'Subwoofer cables',
      units: count,
      lots: count,
      lot: 'each',
      listLotUsd: null,
      availability: null,
      query: 'subwoofer cable RCA',
    }));
  }

  // Totals over the priced lines, so they add up: an unpriced cable's
  // shipping waits with it until it has a price.
  const priced = items.filter((i) => i.total !== null);
  const sum = (f: (i: OrderItem) => number) => cents(priced.reduce((t, i) => t + f(i), 0));
  return {
    items,
    taxPct,
    totals: {
      base: sum((i) => i.base!),
      promo: sum((i) => i.promo),
      shipping: sum((i) => i.shipping),
      tax: sum((i) => i.tax),
      total: sum((i) => i.total!),
    },
    unpriced: items.filter((i) => i.total === null).length,
  };
}
